import { useState, useMemo } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Stack,
  AppBar,
  Toolbar,
  Link,
  TextField,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import PublicThemeToggle from "./PublicThemeToggle";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { glassTopNav } from "./glass";

/** Design tokens aligned with `Clibbins — Lawn Care AI/index.html` mockup */
const C = {
  bg: "#f4f7f2",
  surface: "#f8faf6",
  surface2: "#ffffff",
  surfaceOffset: "#edf2ea",
  divider: "#d4dccf",
  border: "#c8d4c2",
  text: "#1a2318",
  textMuted: "#556350",
  textFaint: "#98a895",
  primary: "#2d7a3a",
  primaryHover: "#235e2c",
  primaryHighlight: "#d0e8d4",
  accent: "#c4680a",
  accentHover: "#a35508",
  accentHighlight: "#f5e4ce",
  heroAccentWord: "#7de08a",
  shadowSm: "0 1px 2px rgba(26,35,24,0.06)",
  shadowMd: "0 4px 12px rgba(26,35,24,0.08)",
  shadowLg: "0 12px 32px rgba(26,35,24,0.12)",
  fontDisplay: "'Cabinet Grotesk', 'Helvetica Neue', sans-serif",
  fontBody: "'General Sans', 'Helvetica Neue', sans-serif",
  transition: "180ms cubic-bezier(0.16, 1, 0.3, 1)",
};

/** Dark palette for marketing page when the visitor enables appearance on `/`. */
const MARKETING_DARK_OVERRIDES = {
  bg: "#121a14",
  surface: "#161f18",
  surface2: "#1a241c",
  surfaceOffset: "#141c16",
  divider: "rgba(255,255,255,0.1)",
  border: "rgba(255,255,255,0.14)",
  text: "rgba(255,255,255,0.92)",
  textMuted: "rgba(255,255,255,0.62)",
  textFaint: "rgba(255,255,255,0.45)",
  primary: "#8fd99a",
  primaryHover: "#a8e8b2",
  primaryHighlight: "rgba(143,217,154,0.2)",
  heroAccentWord: "#b8f5c4",
  accent: "#e8943a",
  accentHover: "#f0a855",
  accentHighlight: "rgba(232,148,58,0.22)",
  shadowSm: "0 1px 2px rgba(0,0,0,0.45)",
  shadowMd: "0 4px 12px rgba(0,0,0,0.4)",
  shadowLg: "0 12px 32px rgba(0,0,0,0.5)",
};

const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
`;

/** Text wordmark — matches app shell liquid-glass header styling */
function ClibbinsTextLogo({ variant = "header", tokens = C }) {
  const isFooter = variant === "footer";
  return (
    <Typography
      component="span"
      sx={{
        fontFamily: tokens.fontDisplay,
        fontWeight: 800,
        fontSize: isFooter ? { xs: "1.15rem", sm: "1.25rem" } : { xs: "1.28rem", sm: "1.48rem" },
        letterSpacing: "-0.04em",
        color: tokens.primary,
        lineHeight: 1,
        ...(!isFooter ? { textShadow: "0 1px 0 rgba(255,255,255,0.88)" } : {}),
      }}
    >
      Clibbins
    </Typography>
  );
}

const problemCards = [
  { icon: "📞", title: "Voicemail graveyard", body: "Phone rings while you're on a mower, and that lead goes to voicemail forever. By the time you're back in the truck, they've already booked someone else." },
  { icon: "📩", title: "Scattered lead chaos", body: "Quote requests pile up in texts, Facebook messages, and website forms. You're running three inboxes in the field and losing track of every other one." },
  { icon: "👻", title: "The ghosting epidemic", body: "Customers disappear after one estimate because nobody has time to follow up 3–4 times. The money was there. The timing just slipped." },
  { icon: "💬", title: "Group chat slip-throughs", body: "Crews get rescheduled in a group chat and something always slips through. One job missed. One customer furious. One 1-star review." },
];

const steps = [
  {
    n: "1",
    icon: "📋",
    title: "Capture the quote online",
    body: "Customers use your branded quote page: map-based address search, optional lawn outline, services from your catalog, and their contact details. You receive one structured request by email—no chasing scraps from texts and voicemails for the basics.",
  },
  {
    n: "2",
    icon: "✉️",
    title: "Respond while you're still in the field",
    body: "Today Clibbins delivers every Fast quote submission straight to the inbox you configure. Timed follow-up texts and emails are on our roadmap so prospects hear from you consistently without a separate drip tool.",
  },
  {
    n: "3",
    icon: "💼",
    title: "Track jobs, bill Clibbins, connect Stripe",
    body: "Move accepted work into your job list with clear statuses. Subscribe to Clibbins through Stripe and complete Stripe Express onboarding so payouts go to your account as homeowner checkout rolls out. Deeper crew routing and calendar sync are planned.",
  },
];

/** Shipped capabilities — see Documentation/marketing-product-parity.md */
const featureRowsLive = [
  {
    title: "Guided quotes built for lawns",
    desc: "Multi-property addresses, optional satellite outline, yard area estimates, and the services you define in your catalog—customers submit once; you get the full picture by email.",
  },
  {
    title: "Operator dashboard",
    desc: "Website hero and branding, embeddable quote experience, service catalog, job list with statuses, and account settings—organized tabs instead of scattered tools.",
  },
  {
    title: "Stripe: your plan + Connect",
    desc: "Pay for Clibbins on a monthly subscription. Connect Stripe Express now so your business is ready when in-app homeowner payments ship; funds route to your Stripe account.",
  },
];

/** Planned — copy stays honest; build order in docs */
const featureRowsRoadmap = [
  {
    title: "Automated follow-up",
    desc: "Planned: reminders to prospects after a quote so fewer leads go cold without manual nudging.",
  },
  {
    title: "Crew scheduling & routing",
    desc: "Planned: assign routes, balance crews, and see the day at a glance—beyond today's job status list.",
  },
  {
    title: "Recurring visits & reminders",
    desc: "Planned: repeat schedules and automatic reminders so customers and crews stay aligned.",
  },
  {
    title: "Broader inbox & crew mobile",
    desc: "Planned: bring more channels into one place and a focused field experience—today, quote leads are email-first with SMS preferences reserved for when providers are wired.",
  },
];

const pricingTiers = [
  {
    planKey: "solo",
    name: "Solo Mower",
    crew: "1 crew · Owner-operator",
    price: "$49",
    priceSub: "/mo",
    featured: false,
    items: [
      "Unlimited Fast quote requests from your page",
      "Maps & multi-lot quoting",
      "14-day trial, then Stripe subscription",
      "Stripe Express onboarding (homeowner checkout next)",
    ],
  },
  {
    planKey: "growing",
    name: "Growing Company",
    crew: "Up to 5 crews",
    price: "$149",
    priceSub: "/mo",
    featured: true,
    badge: "Most Popular",
    items: [
      "Everything in Solo",
      "Job list & status workflow",
      "Branding, embed & catalog",
      "Stripe Connect — ready for payouts; customer checkout rolling out",
      "Priority support",
    ],
  },
  {
    planKey: "enterprise",
    name: "Big Operation",
    crew: "6+ crews · Custom",
    price: "Custom",
    priceSub: "pricing",
    featured: false,
    items: [
      "Everything in Growing",
      "Volume & multi-location (contact us)",
      "Custom integrations (by arrangement)",
      "Onboarding & success (by arrangement)",
    ],
  },
];

function btnAccentSx(extra = {}, tk = C) {
  return {
    fontFamily: tk.fontDisplay,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    textTransform: "none",
    borderRadius: 9999,
    px: 3,
    py: 1.25,
    bgcolor: tk.accent,
    color: "#fff",
    border: `2px solid ${tk.accent}`,
    boxShadow: "none",
    "&:hover": {
      bgcolor: tk.accentHover,
      borderColor: tk.accentHover,
      transform: "translateY(-1px)",
      boxShadow: tk.shadowMd,
    },
    transition: `all ${tk.transition}`,
    ...extra,
  };
}

function btnGhostLightSx(tk = C) {
  return {
    fontFamily: tk.fontDisplay,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 9999,
    px: 3,
    py: 1.25,
    bgcolor: "rgba(255,255,255,0.12)",
    color: "#fff",
    border: "2px solid rgba(255,255,255,0.35)",
    "&:hover": { bgcolor: "rgba(255,255,255,0.18)", borderColor: "rgba(255,255,255,0.5)" },
    transition: `all ${tk.transition}`,
  };
}

/**
 * B2B marketing page — visual design matches `Clibbins — Lawn Care AI` mockup (grass hero, tokens, typography).
 * Customer quote app: `/quote`.
 */
export default function MarketingLanding() {
  const theme = useTheme();
  const P = useMemo(
    () => (theme.palette.mode === "dark" ? { ...C, ...MARKETING_DARK_OVERRIDES } : C),
    [theme.palette.mode]
  );
  const [formDone, setFormDone] = useState(false);

  const onEarlyAccess = (e) => {
    e.preventDefault();
    setFormDone(true);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: P.bg,
        color: P.text,
        fontFamily: P.fontBody,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          ...glassTopNav,
          borderRadius: 0,
        }}
      >
        <Toolbar
          component="nav"
          aria-label="Main"
          sx={{
            maxWidth: 1200,
            width: "100%",
            mx: "auto",
            px: { xs: 2, sm: 3 },
            minHeight: 64,
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            aria-label="Clibbins home"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              flexShrink: 0,
              py: 0.5,
              px: 1.25,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.55)",
              bgcolor: "rgba(255,255,255,0.35)",
              backdropFilter: "blur(18px) saturate(180%)",
              WebkitBackdropFilter: "blur(18px) saturate(180%)",
              boxShadow: `
                inset 0 1px 0 rgba(255, 255, 255, 0.92),
                0 2px 10px rgba(12, 48, 28, 0.06)
              `,
              transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.52)",
                borderColor: "rgba(255,255,255,0.78)",
              },
            }}
          >
            <ClibbinsTextLogo tokens={P} />
          </Box>

          <Stack direction="row" spacing={3} sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
            <Link href="#how-it-works" underline="none" sx={{ fontSize: "0.875rem", fontWeight: 500, color: P.textMuted, "&:hover": { color: P.text } }}>
              How it works
            </Link>
            <Link href="#features" underline="none" sx={{ fontSize: "0.875rem", fontWeight: 500, color: P.textMuted, "&:hover": { color: P.text } }}>
              Features
            </Link>
            <Link href="#pricing" underline="none" sx={{ fontSize: "0.875rem", fontWeight: 500, color: P.textMuted, "&:hover": { color: P.text } }}>
              Pricing
            </Link>
            <Link href="#easter" underline="none" sx={{ fontSize: "0.875rem", fontWeight: 500, color: P.textMuted, "&:hover": { color: P.text } }}>
              Our name
            </Link>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <PublicThemeToggle showLabel={false} />
            <Button component={RouterLink} to="/quote" color="inherit" sx={{ fontWeight: 600, textTransform: "none", display: { xs: "none", sm: "inline-flex" } }}>
              Customer quotes
            </Button>
            <Button component={RouterLink} to="/login" sx={btnAccentSx({ py: 1, px: 2.5, fontSize: "0.875rem" }, P)}>
              Get early access
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero — grass background + gradient overlay (mockup) */}
      <Box
        component="section"
        aria-label="Hero"
        sx={{
          position: "relative",
          minHeight: { xs: "85vh", md: "90vh" },
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          py: { xs: 6, md: 10 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: `url(${process.env.PUBLIC_URL || ""}/hero-lawn.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background:
              "linear-gradient(135deg, rgba(10, 25, 10, 0.82) 0%, rgba(20, 50, 20, 0.70) 50%, rgba(10, 35, 10, 0.85) 100%)",
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ maxWidth: 780 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                display: "inline-flex",
                mb: 3,
                px: 2,
                py: 0.75,
                borderRadius: 9999,
                bgcolor: "rgba(90,179,106,0.18)",
                border: "1px solid rgba(90,179,106,0.35)",
                color: "#9deeab",
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#5ab36a",
                  animation: `${pulseDot} 2s ease infinite`,
                }}
              />
              <span>Built for lawn & landscape crews</span>
            </Stack>

            <Typography
              component="h1"
              sx={{
                fontFamily: P.fontDisplay,
                fontWeight: 900,
                fontSize: { xs: "2.5rem", sm: "3.25rem", md: "clamp(2.5rem, 1rem + 4vw, 5rem)" },
                lineHeight: 1,
                letterSpacing: "-0.03em",
                color: "#fff",
                mb: 3,
              }}
            >
              Turn web quote requests into real jobs with{" "}
              <Box component="span" sx={{ color: P.heroAccentWord }}>
                Clibbins
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "1.05rem", md: "1.2rem" },
                color: "rgba(255,255,255,0.82)",
                maxWidth: 600,
                lineHeight: 1.55,
                mb: 2,
              }}
            >
              A guided quote page and operator dashboard that turn web leads into structured requests—so you spend less time on the phone and more time in the yard.
            </Typography>

            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "rgba(255,255,255,0.55)",
                fontStyle: "italic",
                maxWidth: 540,
                mb: 4,
              }}
            >
              Built for lawn and landscape crews. Not general contractors. Not salons. Just the folks dodging grass clibbins all day.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
              <Button component={RouterLink} to="/login" size="large" sx={btnAccentSx({ px: 4, py: 1.5, fontSize: "1rem" }, P)}>
                Start free 14-day trial
              </Button>
              <Button component={RouterLink} to="/quote" size="large" sx={btnGhostLightSx(P)}>
                See Clibbins in action
              </Button>
            </Stack>

            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>
              Out there fighting grass clibbins? We'll fight your inbox.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Social proof strip */}
      <Box
        component="section"
        sx={{
          bgcolor: P.surface,
          borderBlock: `1px solid ${P.divider}`,
          py: 5,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3} alignItems="center">
            <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, textAlign: "center", fontWeight: 500 }}>
              Built for owner-operators and small lawn teams who can&apos;t live at the inbox
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2} justifyContent="center" useFlexGap>
              {["Owner-operators", "Small crews", "Residential lawn", "Landscaping teams", "Municipal & route pros"].map((name) => (
                <Box
                  key={name}
                  sx={{
                    fontFamily: P.fontDisplay,
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    color: P.textFaint,
                    opacity: 0.85,
                    px: 2,
                    py: 0.75,
                    border: `1px solid ${P.border}`,
                    borderRadius: 1,
                    bgcolor: P.surface2,
                  }}
                >
                  {name}
                </Box>
              ))}
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={{ xs: 4, sm: 8 }} justifyContent="center" useFlexGap>
              {[
                { n: "3.4×", l: "more quotes sent" },
                { n: "68%", l: "faster booking" },
                { n: "$0", l: "leads lost to voicemail" },
              ].map((s) => (
                <Box key={s.l} textAlign="center">
                  <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 900, fontSize: "1.75rem", color: P.primary, letterSpacing: "-0.03em", lineHeight: 1 }}>
                    {s.n}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: P.textMuted, mt: 0.5 }}>{s.l}</Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Problem section */}
      <Box component="section" id="problem" sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: P.primary, mb: 2 }}>
            The real hazard isn't grass clibbins…
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: P.fontDisplay,
              fontWeight: 900,
              fontSize: { xs: "1.85rem", md: "2.5rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
              mb: 3,
            }}
          >
            It's the quotes and follow-ups
            <br />
            you never send
          </Typography>
          <Typography sx={{ color: P.textMuted, maxWidth: "56ch", mb: 6, lineHeight: 1.6 }}>
            Every missed call is a customer who called your competitor next. Every unsent follow-up is money sitting in the grass.
          </Typography>

          <Grid container spacing={2}>
            {problemCards.map((p) => (
              <Grid item xs={12} sm={6} key={p.title}>
                <Box
                  sx={{
                    bgcolor: P.surface,
                    border: `1px solid ${P.border}`,
                    borderRadius: 2,
                    p: 3,
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    height: "100%",
                    transition: `box-shadow ${P.transition}, transform ${P.transition}`,
                    "&:hover": { boxShadow: P.shadowMd, transform: "translateY(-2px)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      bgcolor: P.accentHighlight,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontSize: "1.25rem",
                    }}
                  >
                    {p.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, mb: 1 }}>{p.title}</Typography>
                    <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, lineHeight: 1.55 }}>{p.body}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              mt: 6,
              bgcolor: P.primaryHighlight,
              borderLeft: `3px solid ${P.primary}`,
              borderRadius: 2,
              py: 2.5,
              px: 3,
              fontFamily: P.fontDisplay,
              fontWeight: 700,
              fontSize: "1.1rem",
              color: P.primaryHover,
              letterSpacing: "-0.02em",
            }}
          >
            "You keep the roads clear of clibbins. We keep your pipeline clear of chaos."
          </Box>
        </Container>
      </Box>

      {/* How it works */}
      <Box id="how-it-works" component="section" sx={{ bgcolor: P.surface, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" maxWidth={640} mx="auto" mb={8}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: P.primary, mb: 2 }}>
              How Clibbins captures web quotes
            </Typography>
            <Typography component="h2" sx={{ fontFamily: P.fontDisplay, fontWeight: 900, fontSize: { xs: "1.85rem", md: "2.35rem" }, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
              Three steps. Less desk, more mower.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((s) => (
              <Grid item xs={12} md={4} key={s.n}>
                <Box
                  textAlign="center"
                  sx={{
                    p: 3,
                    bgcolor: P.surface2,
                    border: `1px solid ${P.border}`,
                    borderRadius: 3,
                    boxShadow: P.shadowSm,
                    height: "100%",
                    transition: `box-shadow ${P.transition}, transform ${P.transition}`,
                    "&:hover": { boxShadow: P.shadowLg, transform: "translateY(-4px)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: P.primary,
                      color: "#fff",
                      fontFamily: P.fontDisplay,
                      fontWeight: 900,
                      fontSize: "1.25rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2.5,
                      boxShadow: `0 0 0 6px ${P.primaryHighlight}`,
                    }}
                  >
                    {s.n}
                  </Box>
                  <Typography sx={{ fontSize: "2rem", mb: 1.5 }}>{s.icon}</Typography>
                  <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.02em", mb: 1.5 }}>
                    {s.title}
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, lineHeight: 1.6 }}>{s.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Stack direction="row" flexWrap="wrap" gap={2} justifyContent="center" sx={{ mt: 6 }} useFlexGap>
            {[
              ["⚡", "Guided map-based quotes"],
              ["📬", "Leads straight to your inbox"],
              ["💳", "Stripe plan & Connect"],
            ].map(([icon, label]) => (
              <Stack
                key={label}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  bgcolor: P.surface,
                  border: `1px solid ${P.border}`,
                  borderRadius: 9999,
                  px: 2.5,
                  py: 1.25,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                <span>{icon}</span>
                <span>{label}</span>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Features + dashboard image */}
      <Box id="features" component="section" sx={{ py: { xs: 8, md: 10 }, bgcolor: P.bg }}>
        <Container maxWidth="lg">
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: P.primary, mb: 2 }}>
            Built for the field, not the office
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: P.fontDisplay,
              fontWeight: 900,
              fontSize: { xs: "1.85rem", md: "2.35rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
              mb: 6,
            }}
          >
            What you can use in Clibbins today
            <br />
            —and what we&apos;re building next
          </Typography>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: P.primary,
                  }}
                >
                  Available now
                </Typography>
                {featureRowsLive.map((f) => (
                  <Box
                    key={f.title}
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      p: 2,
                      bgcolor: P.surface,
                      border: `1px solid ${P.border}`,
                      borderRadius: 2,
                      transition: `box-shadow ${P.transition}, border-color ${P.transition}`,
                      "&:hover": { boxShadow: P.shadowMd, borderColor: P.primary },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: P.primary,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, mb: 0.5 }}>{f.title}</Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, lineHeight: 1.5 }}>{f.desc}</Typography>
                    </Box>
                  </Box>
                ))}
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: P.textMuted,
                    pt: 1,
                  }}
                >
                  Roadmap
                </Typography>
                {featureRowsRoadmap.map((f) => (
                  <Box
                    key={f.title}
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      p: 2,
                      bgcolor: P.surfaceOffset,
                      border: `1px dashed ${P.border}`,
                      borderRadius: 2,
                      transition: `box-shadow ${P.transition}, border-color ${P.transition}`,
                      "&:hover": { boxShadow: P.shadowSm, borderColor: P.primary },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: `2px solid ${P.primary}`,
                        color: P.primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: "2px",
                        bgcolor: P.surface2,
                      }}
                    >
                      <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, mb: 0.5 }}>{f.title}</Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, lineHeight: 1.5 }}>{f.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: P.shadowLg,
                  border: `1px solid ${P.border}`,
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL || ""}/dashboard-mockup.png`}
                  alt="Clibbins operator dashboard: website settings, jobs, service catalog, and billing"
                  sx={{ width: "100%", height: "auto", display: "block" }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    bgcolor: "rgba(26,35,24,0.88)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    fontSize: "0.75rem",
                    fontFamily: P.fontDisplay,
                    fontWeight: 600,
                    px: 2,
                    py: 0.75,
                    borderRadius: 9999,
                  }}
                >
                  <span>🌿</span>
                  <span>Job note: watch for clibbins on the shoulder</span>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Name / meme section */}
      <Box
        id="easter"
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: P.surfaceOffset,
          borderBlock: `1px solid ${P.divider}`,
          py: { xs: 8, md: 10 },
          "&::before": {
            content: '"CLIBBINS"',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-12deg)",
            fontFamily: P.fontDisplay,
            fontSize: { xs: "5rem", md: "clamp(5rem, 15vw, 14rem)" },
            fontWeight: 900,
            color: P.primary,
            opacity: 0.04,
            letterSpacing: "-0.04em",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            userSelect: "none",
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: P.primary, mb: 2 }}>
            For the Hog Crankers in the room
          </Typography>
          <Typography component="h2" sx={{ fontFamily: P.fontDisplay, fontWeight: 900, fontSize: { xs: "1.75rem", md: "2.25rem" }, letterSpacing: "-0.03em", mb: 4 }}>
            Yes, we know about
            <br />
            the grass clibbins meme
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 4 }}>
            {[
              { t: "🛣️ Road clibbins", d: "Grass clippings on pavement" },
              { t: "→", d: null, arrow: true },
              { t: "🤖 Business clibbins", d: "Leads & jobs left in the inbox" },
              { t: "→", d: null, arrow: true },
              { t: "✅ Handled", d: "Clibbins clears 'em both", highlight: true },
            ].map((b, i) =>
              b.arrow ? (
                <Typography key={`arrow-${i}`} sx={{ fontSize: "1.5rem", color: P.primary, display: { xs: "none", sm: "block" } }}>
                  →
                </Typography>
              ) : (
                <Box
                  key={`block-${i}`}
                  sx={{
                    bgcolor: P.surface2,
                    border: `1px solid ${P.border}`,
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    flex: 1,
                    maxWidth: 200,
                    boxShadow: P.shadowSm,
                  }}
                >
                  <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, fontSize: "0.9rem", color: b.highlight ? P.primary : P.text, mb: 0.5 }}>
                    {b.t}
                  </Typography>
                  {b.d && (
                    <Typography sx={{ fontSize: "0.7rem", color: P.textFaint }}>{b.d}</Typography>
                  )}
                </Box>
              )
            )}
          </Stack>

          <Typography sx={{ color: P.textMuted, lineHeight: 1.65, mb: 4 }}>
            Clibbins started as a nod to the &quot;grass clibbins, almost haddalayerdown&quot; joke — but the problem is real: what you leave on the road is dangerous, and what you leave in your inbox is expensive.
          </Typography>
          <Button component={RouterLink} to="/login" size="large" sx={btnAccentSx({ px: 4 }, P)}>
            Ready to clear your pipeline? Start free trial
          </Button>
        </Container>
      </Box>

      {/* Pricing */}
      <Box id="pricing" component="section" sx={{ py: { xs: 8, md: 10 }, bgcolor: P.bg }}>
        <Container maxWidth="lg">
          <Box textAlign="center" maxWidth={560} mx="auto" mb={6}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: P.primary, mb: 2 }}>
              No surprise charges
            </Typography>
            <Typography component="h2" sx={{ fontFamily: P.fontDisplay, fontWeight: 900, fontSize: { xs: "1.85rem", md: "2.35rem" }, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
              Simple pricing that scales
              <br />
              with your crew
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, mt: 2 }}>
              Billed monthly through Stripe after trial. Lock in your rate while you stay subscribed. Enterprise and large teams: contact us for volume pricing.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {pricingTiers.map((tier) => (
              <Grid item xs={12} sm={10} md={4} key={tier.name}>
                <Box
                  sx={{
                    position: "relative",
                    bgcolor: tier.featured ? P.primary : P.surface,
                    color: tier.featured ? "#fff" : P.text,
                    border: `1px solid ${tier.featured ? P.primary : P.border}`,
                    borderRadius: 3,
                    p: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    transform: tier.featured ? { md: "scale(1.04)" } : undefined,
                    transition: `box-shadow ${P.transition}, transform ${P.transition}`,
                    "&:hover": { boxShadow: P.shadowLg, transform: tier.featured ? { md: "scale(1.04) translateY(-4px)" } : "translateY(-4px)" },
                  }}
                >
                  {tier.badge && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: P.accent,
                        color: "#fff",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        px: 2,
                        py: 0.25,
                        borderRadius: 9999,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tier.badge}
                    </Box>
                  )}
                  <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 900, fontSize: "1.5rem", letterSpacing: "-0.03em" }}>{tier.name}</Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: tier.featured ? "rgba(255,255,255,0.75)" : P.textMuted }}>{tier.crew}</Typography>
                  <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ my: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: P.fontDisplay,
                        fontWeight: 900,
                        fontSize: tier.price === "Custom" ? "1.85rem" : "2.35rem",
                        letterSpacing: "-0.03em",
                        color: tier.featured ? "#fff" : P.text,
                        lineHeight: 1,
                      }}
                    >
                      {tier.price}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        color: tier.featured ? "rgba(255,255,255,0.8)" : P.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      {tier.priceSub}
                    </Typography>
                  </Stack>
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    {tier.items.map((item) => (
                      <Stack direction="row" spacing={1.5} key={item} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            bgcolor: tier.featured ? "rgba(255,255,255,0.25)" : P.primaryHighlight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: "2px",
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 12, color: tier.featured ? "#fff" : P.primary }} />
                        </Box>
                        <Typography sx={{ fontSize: "0.875rem", color: tier.featured ? "rgba(255,255,255,0.9)" : P.textMuted }}>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  {tier.planKey === "enterprise" ? (
                    <Button
                      component="a"
                      href="#early-access"
                      fullWidth
                      sx={{
                        mt: "auto",
                        fontFamily: P.fontDisplay,
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 9999,
                        textDecoration: "none",
                        bgcolor: "transparent",
                        color: P.text,
                        border: `2px solid ${P.border}`,
                        "&:hover": { bgcolor: P.surfaceOffset, borderColor: P.textMuted },
                      }}
                    >
                      Request early access
                    </Button>
                  ) : (
                    <Button
                      component={RouterLink}
                      to="/login"
                      state={{ from: { pathname: "/dashboard/billing" } }}
                      fullWidth
                      sx={{
                        mt: "auto",
                        fontFamily: P.fontDisplay,
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 9999,
                        ...(tier.featured
                          ? {
                              bgcolor: "rgba(255,255,255,0.15)",
                              color: "#fff",
                              border: "2px solid rgba(255,255,255,0.35)",
                              "&:hover": { bgcolor: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.6)" },
                            }
                          : {
                              bgcolor: "transparent",
                              color: P.text,
                              border: `2px solid ${P.border}`,
                              "&:hover": { bgcolor: P.surfaceOffset, borderColor: P.textMuted },
                            }),
                      }}
                    >
                      Sign in to subscribe
                    </Button>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA — grass texture at low opacity (mockup) */}
      <Box
        id="early-access"
        component="section"
        aria-label="Early access"
        sx={{
          position: "relative",
          bgcolor: P.primary,
          color: "#fff",
          textAlign: "center",
          py: { xs: 8, md: 12 },
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${process.env.PUBLIC_URL || ""}/hero-lawn.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.08,
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: P.fontDisplay,
              fontWeight: 900,
              fontSize: { xs: "1.75rem", md: "2.35rem" },
              letterSpacing: "-0.03em",
              color: "#fff",
              mb: 2,
            }}
          >
            Stop losing jobs between the mower and your inbox
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", maxWidth: 520, mx: "auto", mb: 4, lineHeight: 1.6 }}>
            Let Clibbins collect every web quote, organize jobs, and handle operator billing—so you stay in the yard instead of rebuilding leads from voicemails.
          </Typography>

          {formDone ? (
            <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, color: P.primaryHighlight }}>
              You're on the list! We'll be in touch.
            </Typography>
          ) : (
            <Box component="form" onSubmit={onEarlyAccess} sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480, mx: "auto" }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  required
                  name="email"
                  type="email"
                  placeholder="Work email"
                  fullWidth
                  InputProps={{
                    sx: {
                      bgcolor: "rgba(255,255,255,0.12)",
                      borderRadius: 2,
                      color: "#fff",
                      "& input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
                      "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                      "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                    },
                  }}
                  inputProps={{ "aria-label": "Work email" }}
                  variant="outlined"
                />
                <TextField
                  required
                  name="company"
                  placeholder="Company name"
                  fullWidth
                  InputProps={{
                    sx: {
                      bgcolor: "rgba(255,255,255,0.12)",
                      borderRadius: 2,
                      color: "#fff",
                      "& input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
                      "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                    },
                  }}
                  inputProps={{ "aria-label": "Company name" }}
                  variant="outlined"
                />
              </Stack>
              <TextField
                select
                required
                name="crew-size"
                defaultValue=""
                fullWidth
                SelectProps={{ displayEmpty: true }}
                InputProps={{
                  sx: {
                    bgcolor: "rgba(255,255,255,0.12)",
                    borderRadius: 2,
                    color: "#fff",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                  },
                }}
                inputProps={{ "aria-label": "Crew size" }}
                variant="outlined"
              >
                <MenuItem value="" disabled>
                  Crew size…
                </MenuItem>
                <MenuItem value="solo">Just me (solo operator)</MenuItem>
                <MenuItem value="2-3">2–3 crew members</MenuItem>
                <MenuItem value="4-10">4–10 crew members</MenuItem>
                <MenuItem value="11-25">11–25 crew members</MenuItem>
                <MenuItem value="25+">25+ crew members</MenuItem>
              </TextField>
              <Button
                type="submit"
                sx={{
                  fontFamily: P.fontDisplay,
                  fontWeight: 800,
                  bgcolor: "#fff",
                  color: P.primaryHover,
                  borderRadius: 9999,
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "1rem",
                  letterSpacing: "-0.01em",
                  "&:hover": { bgcolor: "#f0f9f0", transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" },
                }}
              >
                Get on the early access list →
              </Button>
            </Box>
          )}

          <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", mt: 3 }}>No credit card required. 14-day free trial. Cancel anytime.</Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Button component={RouterLink} to="/quote" sx={btnGhostLightSx(P)}>
              Open live quote tool
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              sx={{
                fontFamily: P.fontDisplay,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 9999,
                color: "#fff",
                border: "2px solid rgba(255,255,255,0.5)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Operator login
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: P.surface, borderTop: `1px solid ${P.divider}`, py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={5}>
              <Box sx={{ mb: 2 }}>
                <ClibbinsTextLogo variant="footer" tokens={P} />
              </Box>
              <Typography sx={{ fontSize: "0.875rem", color: P.textMuted, maxWidth: 300, lineHeight: 1.6, mb: 2 }}>
                Guided quotes, a clear operator dashboard, and Stripe billing—built for crews who fight grass clibbins every day, not another generic CRM.
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: P.textFaint }}>© {new Date().getFullYear()} Clibbins. All rights reserved.</Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, fontSize: "0.875rem", mb: 2 }}>Product</Typography>
              <Stack spacing={1.5}>
                {["#features", "#pricing", "#how-it-works"].map((h) => (
                  <Link key={h} href={h} underline="none" sx={{ fontSize: "0.875rem", color: P.textMuted, "&:hover": { color: P.primary } }}>
                    {h === "#features" ? "Features" : h === "#pricing" ? "Pricing" : "How it works"}
                  </Link>
                ))}
                <Typography sx={{ fontSize: "0.875rem", color: P.textFaint }}>Docs</Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, fontSize: "0.875rem", mb: 2 }}>Company</Typography>
              <Stack spacing={1.5}>
                <Link href="#easter" underline="none" sx={{ fontSize: "0.875rem", color: P.textMuted, "&:hover": { color: P.primary } }}>
                  Our name
                </Link>
                <Typography sx={{ fontSize: "0.875rem", color: P.textFaint }}>About</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: P.textFaint }}>Blog</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: P.textFaint }}>Contact</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: P.fontDisplay, fontWeight: 700, fontSize: "0.875rem", mb: 2 }}>Legal</Typography>
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: "0.875rem", color: P.textFaint }}>Privacy Policy</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: P.textFaint }}>Terms of Service</Typography>
                <Link component={RouterLink} to="/login" underline="none" sx={{ fontSize: "0.875rem", color: P.textMuted, "&:hover": { color: P.primary } }}>
                  Login
                </Link>
              </Stack>
            </Grid>
          </Grid>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ mt: 5, pt: 3, borderTop: `1px solid ${P.divider}` }}
          >
            <Typography sx={{ fontSize: "0.7rem", color: P.textFaint }}>Built for lawn care. Not for grass clibbins.</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
